import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import masterDataService, { Company, QueryParams } from '../../services/master-data.service';
import { INDIAN_STATES } from '../../types/customer.types';
import { getApiErrorMessage } from '../../components/ui/FormControls';

const GST_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const PAN_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const stateByGstCode = (code: string) => INDIAN_STATES.find((state) => state.gstCode === code);
const stateByName = (name: string) => INDIAN_STATES.find((state) => state.name.toLowerCase() === name.trim().toLowerCase());

type TaxErrors = { gstNumber?: string; panNumber?: string };

// The GST number is optional (an unregistered company has none) but must be well formed and
// agree with the state and the PAN it carries
const checkTaxNumbers = (gstNumber: string, panNumber: string, state: string): TaxErrors => {
  const errors: TaxErrors = {};
  if (gstNumber && !GST_PATTERN.test(gstNumber)) {
    errors.gstNumber = 'A GST number has 15 characters, like 27ABCDE1234F1Z5';
  } else if (gstNumber) {
    const gstState = stateByGstCode(gstNumber.slice(0, 2));
    const chosenState = stateByName(state);
    if (!gstState) errors.gstNumber = 'The first two digits are not a valid GST state code';
    else if (chosenState && chosenState.gstCode !== gstState.gstCode) {
      errors.gstNumber = `This GST number belongs to ${gstState.name}, but the state is ${chosenState.name}`;
    }
  }
  if (panNumber && !PAN_PATTERN.test(panNumber)) {
    errors.panNumber = 'A PAN has 10 characters, like ABCDE1234F';
  } else if (panNumber && GST_PATTERN.test(gstNumber) && gstNumber.slice(2, 12) !== panNumber) {
    errors.panNumber = 'The PAN does not match the PAN inside the GST number';
  }
  return errors;
};

interface CompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  company: Company | null;
  onSave: () => void;
}

const CompanyModal: React.FC<CompanyModalProps> = ({ isOpen, onClose, company, onSave }) => {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    legalName: '',
    address: '',
    city: '',
    state: '',
    country: '',
    pincode: '',
    phone: '',
    mobile: '',
    email: '',
    website: '',
    gstNumber: '',
    panNumber: '',
    tanNumber: '',
    cinNumber: '',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    swiftCode: '',
    baseCurrency: 'INR',
    language: 'en',
    timezone: 'Asia/Kolkata',
    logoUrl: '',
    isPrimary: false,
    isActive: true,
    settings: {}
  });
  const [loading, setLoading] = useState(false);
  const [taxErrors, setTaxErrors] = useState<TaxErrors>({});

  useEffect(() => {
    setTaxErrors({});
    if (company) {
      setFormData({
        code: company.code,
        name: company.name,
        description: company.description || '',
        legalName: company.legalName || '',
        address: company.address,
        city: company.city || '',
        state: company.state || '',
        country: company.country || '',
        pincode: company.pincode || '',
        phone: company.phone || '',
        mobile: company.mobile || '',
        email: company.email || '',
        website: company.website || '',
        gstNumber: company.gstNumber || '',
        panNumber: company.panNumber || '',
        tanNumber: company.tanNumber || '',
        cinNumber: company.cinNumber || '',
        bankName: company.bankName || '',
        accountNumber: company.accountNumber || '',
        ifscCode: company.ifscCode || '',
        swiftCode: company.swiftCode || '',
        baseCurrency: company.baseCurrency,
        language: company.language,
        timezone: company.timezone,
        logoUrl: company.logoUrl || '',
        isPrimary: company.isPrimary,
        isActive: company.isActive,
        settings: company.settings || {}
      });
    } else {
      setFormData({
        code: '',
        name: '',
        description: '',
        legalName: '',
        address: '',
        city: '',
        state: '',
        country: '',
        pincode: '',
        phone: '',
        mobile: '',
        email: '',
        website: '',
        gstNumber: '',
        panNumber: '',
        tanNumber: '',
        cinNumber: '',
        bankName: '',
        accountNumber: '',
        ifscCode: '',
        swiftCode: '',
        baseCurrency: 'INR',
        language: 'en',
        timezone: 'Asia/Kolkata',
        logoUrl: '',
        isPrimary: false,
        isActive: true,
        settings: {}
      });
    }
  }, [company, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = checkTaxNumbers(formData.gstNumber, formData.panNumber, formData.state);
    setTaxErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error(errors.gstNumber || errors.panNumber || 'Check the tax numbers');
      return;
    }
    setLoading(true);

    try {
      if (company) {
        await masterDataService.updateCompany(company.id, formData);
        toast.success('Company updated successfully');
      } else {
        await masterDataService.createCompany(formData);
        toast.success('Company created successfully');
      }
      onSave();
      onClose();
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, 'The company could not be saved'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-screen overflow-y-auto m-4">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">
              {company ? 'Edit Company' : 'Create New Company'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="col-span-2">
                <h3 className="text-lg font-medium mb-4">Basic Information</h3>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Code *
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                  disabled={!!company}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Legal Name
                </label>
                <input
                  type="text"
                  value={formData.legalName}
                  onChange={(e) => setFormData({ ...formData, legalName: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                />
              </div>

              {/* Address Information */}
              <div className="col-span-2">
                <h3 className="text-lg font-medium mb-4 mt-6">Address Information</h3>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address *
                </label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State
                </label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country
                </label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pin Code
                </label>
                <input
                  type="text"
                  value={formData.pincode}
                  onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Contact Information */}
              <div className="col-span-2">
                <h3 className="text-lg font-medium mb-4 mt-6">Contact Information</h3>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile
                </label>
                <input
                  type="text"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Legal Information */}
              <div className="col-span-2">
                <h3 className="text-lg font-medium mb-4 mt-6">Legal Information</h3>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  GST Number
                </label>
                <input
                  type="text"
                  value={formData.gstNumber}
                  maxLength={15}
                  placeholder="27ABCDE1234F1Z5"
                  onChange={(e) => {
                    const gstNumber = e.target.value.toUpperCase().replace(/\s/g, '');
                    const next = { ...formData, gstNumber };
                    // A complete GST number carries the state and the PAN, so fill in what is still empty
                    if (GST_PATTERN.test(gstNumber)) {
                      if (!formData.panNumber.trim()) next.panNumber = gstNumber.slice(2, 12);
                      if (!formData.state.trim()) next.state = stateByGstCode(gstNumber.slice(0, 2))?.name || '';
                    }
                    setFormData(next);
                    setTaxErrors({});
                  }}
                  className={`w-full p-3 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${taxErrors.gstNumber ? 'border-red-500' : 'border-gray-300'}`}
                />
                {taxErrors.gstNumber ? (
                  <p className="mt-1 text-sm text-red-600">{taxErrors.gstNumber}</p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">Leave empty if the company is not registered for GST.</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PAN Number
                </label>
                <input
                  type="text"
                  value={formData.panNumber}
                  maxLength={10}
                  onChange={(e) => {
                    setFormData({ ...formData, panNumber: e.target.value.toUpperCase().replace(/\s/g, '') });
                    setTaxErrors({});
                  }}
                  className={`w-full p-3 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${taxErrors.panNumber ? 'border-red-500' : 'border-gray-300'}`}
                />
                {taxErrors.panNumber && <p className="mt-1 text-sm text-red-600">{taxErrors.panNumber}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  TAN Number
                </label>
                <input
                  type="text"
                  value={formData.tanNumber}
                  onChange={(e) => setFormData({ ...formData, tanNumber: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CIN Number
                </label>
                <input
                  type="text"
                  value={formData.cinNumber}
                  onChange={(e) => setFormData({ ...formData, cinNumber: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Banking Information */}
              <div className="col-span-2">
                <h3 className="text-lg font-medium mb-4 mt-6">Banking Information</h3>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bank Name
                </label>
                <input
                  type="text"
                  value={formData.bankName}
                  onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Number
                </label>
                <input
                  type="text"
                  value={formData.accountNumber}
                  onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  IFSC Code
                </label>
                <input
                  type="text"
                  value={formData.ifscCode}
                  onChange={(e) => setFormData({ ...formData, ifscCode: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SWIFT Code
                </label>
                <input
                  type="text"
                  value={formData.swiftCode}
                  onChange={(e) => setFormData({ ...formData, swiftCode: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* System Settings */}
              <div className="col-span-2">
                <h3 className="text-lg font-medium mb-4 mt-6">System Settings</h3>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Base Currency
                </label>
                <select
                  value={formData.baseCurrency}
                  onChange={(e) => setFormData({ ...formData, baseCurrency: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="INR">INR - Indian Rupee</option>
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Language
                </label>
                <select
                  value={formData.language}
                  onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="en">English</option>
                  <option value="hi">Hindi</option>
                  <option value="mr">Marathi</option>
                  <option value="ta">Tamil</option>
                  <option value="te">Telugu</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Timezone
                </label>
                <select
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Asia/Kolkata">Asia/Kolkata</option>
                  <option value="Asia/Mumbai">Asia/Mumbai</option>
                  <option value="Asia/Delhi">Asia/Delhi</option>
                  <option value="Asia/Chennai">Asia/Chennai</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo URL
                </label>
                <input
                  type="url"
                  value={formData.logoUrl}
                  onChange={(e) => setFormData({ ...formData, logoUrl: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="col-span-2 space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isPrimary}
                    onChange={(e) => setFormData({ ...formData, isPrimary: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Primary Company (used for invoices)</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Active</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end mt-8 pt-6 border-t">
              <button
                type="button"
                onClick={onClose}
                className="mr-3 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-300"
              >
                {loading ? 'Saving...' : company ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const CompanyManagement: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCompanies, setTotalCompanies] = useState(0);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const params: QueryParams = {
        page: currentPage,
        limit: 10,
        search: searchTerm || undefined,
      };

      const response = await masterDataService.getCompanies(params);
      setCompanies(response.data);
      setTotalPages(Math.ceil(response.total / 10));
      setTotalCompanies(response.total);
    } catch (error) {
      toast.error('Failed to fetch companies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, [currentPage, searchTerm]);

  const handleCreateCompany = () => {
    setSelectedCompany(null);
    setModalOpen(true);
  };

  const handleEditCompany = (company: Company) => {
    setSelectedCompany(company);
    setModalOpen(true);
  };

  const handleDeleteCompany = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this company?')) {
      return;
    }

    try {
      await masterDataService.deleteCompany(id);
      toast.success('Company deleted successfully');
      fetchCompanies();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete company');
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchCompanies();
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Company Management</h1>
        <button
          onClick={handleCreateCompany}
          className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          Create New Company
        </button>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <form onSubmit={handleSearch} className="flex gap-4">
            <input
              type="text"
              placeholder="Search companies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 p-2 border border-gray-300 rounded-md"
            />
            <button
              type="submit"
              className="px-4 py-2 text-white bg-gray-600 rounded-md hover:bg-gray-700"
            >
              Search
            </button>
          </form>
        </div>

        {loading ? (
          <div className="p-8 text-center">Loading...</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">Code</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">City</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">State</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">GST Number</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {companies.map((company) => (
                    <tr key={company.id}>
                      <td className="px-4 py-4 text-sm font-medium text-gray-900">{company.code}</td>
                      <td className="px-4 py-4 text-sm text-gray-900">{company.name}</td>
                      <td className="px-4 py-4 text-sm text-gray-500">{company.city || '-'}</td>
                      <td className="px-4 py-4 text-sm text-gray-500">{company.state || '-'}</td>
                      <td className="px-4 py-4 text-sm text-gray-500">{company.gstNumber || '-'}</td>
                      <td className="px-4 py-4 text-sm">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            company.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {company.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <button
                          onClick={() => handleEditCompany(company)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteCompany(company.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex justify-between items-center p-4 border-t">
                <div className="text-sm text-gray-500">
                  Showing {companies.length} of {totalCompanies} companies
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <CompanyModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        company={selectedCompany}
        onSave={fetchCompanies}
      />
    </div>
  );
};

export default CompanyManagement;